/**
 * D-06: one fixed, hand-written Arabic status line per tool, looked up client-side from the
 * `toolStart` frame's `toolName`. NO LLM CALL. That is the whole point of D-06: a status line that
 * costs a model round trip is a status line that arrives after the thing it was describing.
 *
 * KEYED OFF A NAME MAP, NOT BARE STRING LITERALS, mirroring `webAdapter.ts:77`'s `blockKindMap`
 * habit and CLAUDE.md's enum-as-const rule. The map is what makes `TOOL_STATUS_LABELS` a
 * `Record<ChatToolName, string>`: adding a tool to the map without a label is a compile error, so
 * "the label was forgotten" cannot ship silently.
 *
 * THIRTEEN, and the number is load bearing. Six curated tools
 * (`chat-agent/src/engine/v2/toolDefs.ts` — `get_store_by_url`, `search_stores`, `get_reviews`,
 * `get_categories`, `search_reviews`, `request_store_addition`) plus seven deep tools
 * (`chat-agent/src/engine/tools/deep/names.ts` `deepToolNameMap` — `mongo_find`, `mongo_aggregate`,
 * `top_stores`, `store_scorecard`, `compare_stores`, `meili_raw`, `web_search`). `mongo_find` and
 * `mongo_aggregate` are two distinct declarations, not one tool with a mode flag; `names.ts` carries
 * the same warning against reconciling the count downward.
 *
 * WHY THE FRONTEND RESTATES THE NAMES INSTEAD OF IMPORTING THEM. The chat agent is a separate
 * workspace with its own build and its own deploy; the frontend cannot import from it, and the tool
 * name is a WIRE value either way (it arrives on a `toolStart` frame as a string). What protects the
 * pair is the fallback: an unmapped name renders the fallback line rather than nothing and rather
 * than a raw machine identifier, so a new tool on the server degrades to a truthful generic instead
 * of breaking the panel.
 *
 * EVERY LABEL ENDS IN `...` AND NONE PROMISES A RESULT, because a tool may fail. "بدور في المتاجر"
 * is true whether or not anything is found; "لقيت المتاجر" would be a claim the tool has not made
 * yet, rendered before the tool returned.
 *
 * Zero imports, so the no-dash suite can load it under vitest's node environment.
 */

/** The thirteen tool names the panel can be handed, as the `as const` + `keyof typeof` idiom. */
export const chatToolNameMap = {
  // The six curated tools (v2's `buildV2Tools`).
  get_store_by_url: 'get_store_by_url',
  search_stores: 'search_stores',
  get_reviews: 'get_reviews',
  search_reviews: 'search_reviews',
  get_categories: 'get_categories',
  request_store_addition: 'request_store_addition',
  // The seven deep tools (`deepToolNameMap`).
  top_stores: 'top_stores',
  store_scorecard: 'store_scorecard',
  compare_stores: 'compare_stores',
  mongo_find: 'mongo_find',
  mongo_aggregate: 'mongo_aggregate',
  meili_raw: 'meili_raw',
  web_search: 'web_search',
} as const;

export type ChatToolName = keyof typeof chatToolNameMap;

/**
 * The fallback, for any tool this client has never heard of.
 *
 * The wording echoes `PACING_ACK_1_FALLBACK_MESSAGE` ("ثانية واحدة، أنا بدور على المعلومة") on
 * purpose: a visitor who has met ناصح on WhatsApp should hear the same voice on the site.
 */
export const TOOL_STATUS_FALLBACK_LABEL = 'ثانية واحدة، بجمع المعلومة...';

/** Transcribed from `38-UI-SPEC.md` § "Tool-status lines (D-06)", verbatim and binding. */
export const TOOL_STATUS_LABELS: Record<ChatToolName, string> = {
  get_store_by_url: 'بفتح صفحة المتجر...',
  search_stores: 'بدور في المتاجر...',
  get_reviews: 'بقرأ الريفيوهات...',
  search_reviews: 'بدور جوه الريفيوهات...',
  get_categories: 'بشوف الأقسام...',
  request_store_addition: 'بسجل طلب إضافة المتجر...',
  top_stores: 'برتب المتاجر على حسب الريفيوهات...',
  store_scorecard: 'بجمع تقييمات المتجر...',
  compare_stores: 'بقارن المتاجر...',
  mongo_find: 'بدور في البيانات...',
  mongo_aggregate: 'بحسب الأرقام...',
  meili_raw: 'بعمل بحث أوسع...',
  web_search: 'بدور على النت...',
};

/**
 * The lookup the status line renders. Total: an unmapped or absent name gets the fallback, never a
 * raw tool identifier and never an empty row.
 */
export const toolStatusLabel = (toolName: string | null | undefined): string => {
  if (typeof toolName === 'string' && toolName in TOOL_STATUS_LABELS) {
    return TOOL_STATUS_LABELS[toolName as ChatToolName];
  }
  return TOOL_STATUS_FALLBACK_LABEL;
};
